<script lang="ts" setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import LibraryFile from '@/api/Dara/LibraryFile'
import LogoSpinner from '@/components/app/LogoSpinner.vue'
import TutorialVideoPreview from '@/components/app/TutorialVideoPreview.vue'
import { useServiceStatusQuery } from '@/composable/queries/useServiceStatusQuery'
import { useHelpDocsQuery } from '@/composable/queries/Dara/help'
import { routeToPageWithVideoMap } from '@/interfaces/internal-resources/TutorialVideo'
import { VariationType } from '@/models/Programme/VariationType'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'

const appStore = useAppStore()
const authStore = useAuthStore()
const route = useRoute()

const downloading = ref<Record<number, boolean>>({})
const helpKey = ref<string | null>(null)
const helpKeyString = computed(() => helpKey.value ?? '')
const isEnabled = computed(() => helpKey.value !== null && appStore.showQuickHelpDrawer)

const loading = ref(false)

const {
  data: services,
  isLoading: isLoadingServices,
  isError: isErrorServices,
} = useServiceStatusQuery()

const filteredServices = computed(() => {
  return services.value?.filter(service => service.alias) ?? []
})

const {
  data: helpDocsData,
} = useHelpDocsQuery(helpKeyString, isEnabled)

const helpDocs = computed(() => {
  const docs = [...(helpDocsData.value ?? [])]

  return docs.sort((a, b) => {
    const aOrder = (a.help_display_order === 0 ? null : a.help_display_order)
    const bOrder = (b.help_display_order === 0 ? null : b.help_display_order)
    if (aOrder === null && bOrder === null) return 0
    if (aOrder === null) return 1
    if (bOrder === null) return -1

    return aOrder - bOrder
  })
})

const setHelpKey = (key: string) => {
  helpKey.value = key
}

const pageReference = computed(() => {
  const routeName = route.name?.toString()

  return routeName ? (routeToPageWithVideoMap[routeName] ?? null) : null
})

const handleDownload = async (item: Domain.ResourceLibrary.Resources.ApiV3.LibraryFileResource) => {
  downloading.value[item.id] = true

  try {
    await download(item)
  } finally {
    downloading.value[item.id] = false
  }
}

const download = async (libraryFile: Domain.ResourceLibrary.Resources.ApiV3.LibraryFileResource) => {
  const fileUrl = await LibraryFile.download(libraryFile.id)

  const link = document.createElement('a')

  link.href = fileUrl
  link.click()
}

watch(() => route.name, newRouteName => {
  if (newRouteName) {
    setHelpKey(newRouteName.toString())
  }
})

onMounted(() => {
  const routeName = route.name?.toString()

  if (routeName) {
    setHelpKey(routeName)
  }
})
</script>

<template>
  <VNavigationDrawer
    v-model="appStore.showQuickHelpDrawer"
    location="right"
    temporary
    :width="500"
  >
    <VContainer>
      <VRow>
        <VCol cols="10">
          <h2>{{ $t('quick_help.title') }}</h2>
        </VCol>
        <VCol
          cols="2"
          class="d-flex justify-end"
        >
          <VBtn
            icon
            @click="appStore.showQuickHelpDrawer = false"
          >
            <VIcon>mdi-close</VIcon>
          </VBtn>
        </VCol>
      </VRow>
      <VRow>
        <VCol cols="6">
          <VBtn
            color="default"
            href="https://myfuturechoice.com"
            rel="noopener noreferrer"
            target="_blank"
            variant="outlined"
            block
          >
            <VIcon class="mr-2">
              mdi-web
            </VIcon> {{ $t('quick_help.website') }}
          </VBtn>
        </VCol>
        <VCol cols="6">
          <VBtn
            color="default"
            href="mailto:support@myfuturechoice.com"
            rel="noopener noreferrer"
            target="_blank"
            variant="outlined"
            block
          >
            <VIcon class="mr-2">
              mdi-email
            </VIcon> {{ $t('quick_help.email') }}
          </VBtn>
        </VCol>
      </VRow>
      <VRow>
        <VCol
          v-if="loading"
          cols="12"
        >
          <LogoSpinner />
        </VCol>
        <VCol
          v-else
          cols="12"
        >
          <h4>
            {{ $t('quick_help.suggested_guides') }}
          </h4>
          <VList
            v-if="helpDocs.length > 0"
            class="help-docs-list"
            density="compact"
          >
            <VListItem
              v-for="doc in helpDocs"
              :key="doc.id"
              class="quick-help__list-item"
              density="compact"
              @click="handleDownload(doc)"
            >
              <VListItemTitle>
                {{ doc.title }}
              </VListItemTitle>
              <template #prepend>
                <VIcon>mdi-book-open-page-variant</VIcon>
              </template>
            </VListItem>
          </VList>
          <VAlert
            v-else
            class="mt-4 text-small"
            density="compact"
            type="info"
            variant="outlined"
          >
            {{ $t('quick_help.no_guides') }}
          </VAlert>
        </VCol>
      </VRow>
      <VRow v-if="pageReference">
        <VCol cols="12">
          <h4 class="mb-3">
            {{ $t('quick_help.tutorial_videos') }}
          </h4>
          <TutorialVideoPreview :page="pageReference" />
        </VCol>
      </VRow>
      <VRow>
        <VCol cols="12">
          <h4>{{ $t('quick_help.quick_links') }}</h4>
          <VList
            density="compact"
          >
            <VListItem
              :to="{ name: 'resource-library' }"
              class="quick-help__list-item"
            >
              <VListItemTitle>
                {{ $t('page_titles.resource_library') }}
              </VListItemTitle>
              <template #prepend>
                <VIcon>mdi-book-open-page-variant</VIcon>
              </template>
            </VListItem>
            <VListItem
              href="https://www.myfuturechoice.com/webinars-training/"
              target="_blank"
              rel="noopener noreferrer"
              class="quick-help__list-item"
            >
              <VListItemTitle>
                {{ $t('quick_help.webinars_training') }}
              </VListItemTitle>
              <template #prepend>
                <VIcon>mdi-video</VIcon>
              </template>
            </VListItem>
            <VListItem
              href="https://www.myfuturechoice.com/bulletin/"
              target="_blank"
              rel="noopener noreferrer"
              class="quick-help__list-item"
            >
              <VListItemTitle>
                {{ $t('quick_help.latest_news') }}
              </VListItemTitle>
              <template #prepend>
                <VIcon>mdi-newspaper</VIcon>
              </template>
            </VListItem>
          </VList>
        </VCol>
      </VRow>
      <VRow>
        <VCol cols="12">
          <h4>{{ $t('quick_help.service_status') }}</h4>
          <LogoSpinner v-if="isLoadingServices" />
          <VAlert
            v-else-if="isErrorServices"
            type="error"
          >
            {{ $t('quick_help.service_status_error') }}
          </VAlert>
          <VList
            v-else
            class="text-small"
            density="compact"
          >
            <VListItem
              v-for="service in filteredServices"
              :key="service.token"
              class="quick-help__list-item"
            >
              <VListItemTitle>
                {{ service.alias }}
              </VListItemTitle>
              <template #prepend>
                <VIcon
                  size="small"
                  :color="service.down ? 'error' : 'success'"
                >
                  mdi-{{ service.down ? 'alert-circle' : 'check-circle' }}
                </VIcon>
              </template>
              <template #append>
                <span
                  :class="service.uptime > 90 ? 'text-success' : 'text-warning'"
                >
                  {{ $t('quick_help.service_uptime', { uptime: service.uptime }) }}
                </span>
              </template>
            </VListItem>
          </VList>
        </VCol>
      </VRow>
      <VRow>
        <VCol cols="12">
          <h4 class="mb-4">
            {{ $t('quick_help.contact_support') }}
          </h4>
          <div class="ml-4">
            <p class="mb-2">
              <a
                class="text-decoration-underline"
                href="mailto:support@myfuturechoice.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                support@myfuturechoice.com
              </a>
            </p>
            <p class="mb-2">
              <a
                v-if="authStore.organisationRegionVariationType === VariationType.EQ"
                class="text-decoration-underline"
                href="tel:+35319036810"
              >
                Ireland: +35319036810
              </a>
              <a
                v-else
                class="text-decoration-underline"
                href="tel:+441362688722"
              >
                +44 (0)1362 688722
              </a>
            </p>
          </div>
        </VCol>
      </VRow>
    </VContainer>
  </VNavigationDrawer>
</template>

<style scoped>
.help-docs-list .v-list-item {
  color: inherit;
}

.help-docs-list .v-list-item:hover {
  color: rgb(var(--v-theme-secondary));
}

.quick-help__list-item {
  min-height: 30px !important;
  font-size: 0.875rem;

  .v-list-item-title {
    font-size: 0.875rem;
  }
}

.quick-help__list-item.v-list-item--link {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}
</style>
